/**
 * Pure helpers for the Reports page: the date range, and how numbers read.
 */

export type Granularity = 'DAY' | 'WEEK' | 'MONTH';

export type RangePreset = '7d' | '30d' | '90d' | '365d' | 'custom';

export const PRESET_DAYS: Record<Exclude<RangePreset, 'custom'>, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '365d': 365,
};

export const PRESET_LABELS: Record<RangePreset, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  '365d': 'Last 12 months',
  custom: 'Custom',
};

/** The backend refuses longer ranges (REPORT_RANGE_INVALID). */
export const MAX_RANGE_DAYS = 400;

const DAY_MS = 24 * 60 * 60 * 1000;

export function startOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

export function endOfDay(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(23, 59, 59, 999);
  return copy;
}

/** The last `days` days, today included, as local whole days. */
export function presetRange(days: number, now: Date = new Date()): { from: Date; to: Date } {
  const from = startOfDay(now);
  from.setDate(from.getDate() - (days - 1));
  return { from, to: endOfDay(now) };
}

export function spanDays(from: Date, to: Date): number {
  return Math.round((endOfDay(to).getTime() - startOfDay(from).getTime()) / DAY_MS);
}

/** A sensible period for a span: days up to a month, weeks up to a quarter, then months. */
export function defaultGranularity(from: Date, to: Date): Granularity {
  const days = spanDays(from, to);
  if (days <= 31) return 'DAY';
  if (days <= 120) return 'WEEK';
  return 'MONTH';
}

/** The browser's zone, so the server cuts days where the date picker does. */
export function browserTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

export function formatCount(value: number | null | undefined): string {
  return (value ?? 0).toLocaleString('en-US');
}

export function formatPercent(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return '—';
  const percent = rate * 100;
  return `${percent < 10 && percent > 0 ? percent.toFixed(1) : Math.round(percent)}%`;
}

/**
 * A part of a total as a percentage that does not round to a lie: 200 of 201
 * is 99%, not 100%, and 1 of 9,953 is "<1%", not 0%.
 */
export function shareOf(value: number, total: number): { label: string; width: number } {
  const exact = (value / total) * 100;
  if (value > 0 && exact < 1) return { label: "<1%", width: exact };
  const rounded = value < total ? Math.min(99, Math.round(exact)) : Math.round(exact);
  return { label: `${rounded}%`, width: Math.min(100, exact) };
}

/** 0 → "0s", 75 → "1m 15s", 3700 → "1h 1m", 200000 → "55h 33m". */
export function formatDuration(seconds: number | null | undefined): string {
  const total = Math.max(0, Math.round(seconds ?? 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = total % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${rest}s`;
  return `${rest}s`;
}

/**
 * A period start as the server sends it (`YYYY-MM-DD`, already in the report's
 * zone), labelled for its granularity. Parsed as a calendar date, never through
 * the browser's zone, which would shift it a day west of UTC.
 */
export function formatPeriod(period: string, granularity: Granularity): string {
  const [year, month, day] = period.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (granularity === 'MONTH') {
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  }
  const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return granularity === 'WEEK' ? `Week of ${label}` : label;
}

export function formatDateTime(value: string | Date): string {
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export interface ReportMemberLike {
  firstName: string;
  lastName: string;
  username: string;
  isDeleted?: boolean;
}

export function memberName(member: ReportMemberLike | null | undefined): string {
  if (!member) return '';
  const name = `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim() || member.username;
  return member.isDeleted ? `${name} (deleted)` : name;
}
